import { dataService } from '../../data';

import { DataObject, ObjectOptions } from '../base';
import { CreateObject } from '../item';

import { Pinpad } from './pinpad';
import { Rasppi } from './rasppi';
import { RasppiConnect } from './rasppiconnect';
import { Till } from './till';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { SessionType } from '../types';
import { RasppiConnectType } from '../types';
import { PinpadType } from '../types';

const _schema = {
    name: 'POSDEVICE',
    fields: [
        { name: 'status', type: 'string', default: "'AC'" },
        { name: 'calc', type: 'string' },
        { name: 'uuid', type: 'string' },
        { name: 'name', type: 'string' },
    ],
    relate: [
        { direction: '<', target: 'SESSION', by: 'posdevice', name: 'session', reason: 'sessions', child: false, class: SessionType },
        { direction: '<', target: 'RASPPICONNECT', by: 'posdevice', name: 'connect', reason: 'connects', child: true, class: RasppiConnectType },
        { direction: '<', target: 'PINPAD', by: 'posdevice', name: 'pinpad', reason: 'pinpads', child: true, class: PinpadType },
    ]
} as const;

const _PosDeviceClass = CreateObject(_schema);
if (!_PosDeviceClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class PosDevice extends _PosDeviceClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): PosDevice {
        return this._Copy(store) as PosDevice;
    }


    /**********************************/
    /* CUSTOM MEMBERS                */
    /**********************************/

    get IsValid(): boolean {
        return this.status == 'AC';
    }

    get IsCurrent(): boolean {
        const _sessionDevice = this.data.session?.device;
        if (!(_sessionDevice instanceof PosDevice)){
            return false;
        }

        if (this === _sessionDevice){
            return true;
        }

        return !!this.objid && !!_sessionDevice.objid && (this.objid === _sessionDevice.objid);
    }

    /**
     * Active RASPPICONNECT this device uses on the current place slice (`hassvc`).
     * Single object-level source for the session connect; `service` and
     * `Place.RequiresLocalBackup` consume this getter.
     */
    get connect(): RasppiConnect | null {
        if (!this.IsValid){
            return null;
        }

        const _place = this.data.place;
        if (!_place){
            return null;
        }

        for (const _connect of this.connects || []){
            if (!_connect.IsValid || !_connect.hassvc){
                continue;
            }

            const _service = _connect.rasppi;
            if (_service?.IsValid && _service.place === _place){
                return _connect;
            }
        }

        return null;
    }

    /** Active service this device uses on the current place slice (via RASPPICONNECT hassvc). */
    get service(): Rasppi | null {
        return this.connect?.rasppi ?? null;
    }

    /** Active till whose service this device uses on the current place slice. */
    get till(): Till | null {
        return this.service?.till ?? null;
    }

    /**
     * Active payment terminal linked to this device on the current place slice
     * (`PINPAD.device` + {@link Pinpad.IsValid}, service of `data.place`).
     */
    get pinpad(): Pinpad | null {
        if (!this.IsValid){
            return null;
        }

        const _place = this.data.place;
        if (!_place){
            return null;
        }

        for (const _terminal of this.pinpads || []){
            if (_terminal.IsValid && _terminal.rasppi?.place === _place){
                return _terminal;
            }
        }

        return null;
    }

    /** Whether this device is a TPV terminal on the current place (active connect to a service of this place). */
    get IsPos(): boolean {
        return !!this.uuid && !!this.service;
    }

    /**
     * True when a non-deleted PTF session on this device is live (`Session.IsActive`).
     * Same `sessions.some(…IsActive)` shape as table/user presence; not cached (clock).
     * Prefixed `Ptf` so it does not clash with RASPPICONNECT / {@link IsPos}.
     */
    get PtfConnected(): boolean {
        return (this.sessions ?? []).some(
            (_session) => _session.mode === 'PTF' && _session.status !== 'DE' && _session.IsActive,
        );
    }

    /**
     * Latest `touch` among non-deleted PTF sessions (including stale).
     * Not cached: same clock dependency as {@link PtfConnected}.
     */
    get PtfTouch(): Date | null {
        let _best: Date | null = null;

        for (const _session of this.sessions ?? []){
            if (_session.mode !== 'PTF' || _session.status === 'DE'){
                continue;
            }

            const _touch = _session.touch ?? null;
            if (_touch && (!_best || _touch.getTime() > _best.getTime())){
                _best = _touch;
            }
        }

        return _best;
    }
}
